import { useSession } from '@pickleballinc/ui/components/global/SessionProvider';
import { useCallback, useState } from 'react';

import { TOURNAMENT_FAVORITES_MODULE_FOR } from '@/modules/blog/constants';
import { ORGANIZATION_MODULE_FOR } from '@/modules/players/v2/tabs/constants';
import { TEAM_LEAGUES_MODULE } from '@/modules/results/constants';
import { addToFavorites } from '@/utils/actions/blog/addToFavorites';
import { removeFavorites } from '@/utils/actions/blog/removeFavorites';

interface UseFavoriteToggleParams {
  moduleUuid: string;
  moduleFor: number;
  initialIsFavorite?: boolean;
  onRefetch?: () => void;
}

interface UseFavoriteToggleResult {
  isFavorited: boolean;
  isDisabled: boolean;
  handleFavoriteToggle: (event: React.MouseEvent) => Promise<void>;
}

export function useFavoriteToggle({
  moduleUuid,
  moduleFor,
  initialIsFavorite = false,
  onRefetch,
}: UseFavoriteToggleParams): UseFavoriteToggleResult {
  const [isFavorited, setIsFavorited] = useState(initialIsFavorite);
  const [isDisabled, setIsDisabled] = useState(false);

  const handleFavoriteToggle = useCallback(
    async (event: React.MouseEvent) => {
      event.preventDefault();
      if (isDisabled) return;
      setIsDisabled(true);

      const newFavoritedState = !isFavorited;
      setIsFavorited(newFavoritedState);

      try {
        if (newFavoritedState) {
          await addToFavorites({ moduleFor, moduleUuid });
        } else {
          await removeFavorites({ moduleFor, moduleUuid });
        }
        onRefetch?.();
      } catch {
        setIsFavorited(!newFavoritedState);
      } finally {
        setIsDisabled(false);
      }
    },
    [isDisabled, isFavorited, moduleFor, moduleUuid, onRefetch]
  );

  return { isFavorited, isDisabled, handleFavoriteToggle };
}

export function useTournamentFavorite(
  id: string,
  initialIsFavorite?: boolean,
  onRefetch?: () => void
) {
  const { session } = useSession();

  const { isFavorited, isDisabled, handleFavoriteToggle } = useFavoriteToggle({
    moduleUuid: id,
    moduleFor: TOURNAMENT_FAVORITES_MODULE_FOR,
    initialIsFavorite,
    onRefetch,
  });

  return {
    isFavorite: isFavorited,
    isFavoriteDisabled: isDisabled,
    isUserLoggedIn: !!session?.user?.uuid,
    onToggle: (event: React.MouseEvent) => handleFavoriteToggle(event),
  };
}

export function useClubFavorite(id: string, initialIsFavorite?: boolean, onRefetch?: () => void) {
  const { session } = useSession();

  const { isFavorited, isDisabled, handleFavoriteToggle } = useFavoriteToggle({
    moduleUuid: id,
    moduleFor: ORGANIZATION_MODULE_FOR,
    initialIsFavorite,
    onRefetch,
  });

  return {
    isFavorite: isFavorited,
    isFavoriteDisabled: isDisabled,
    isUserLoggedIn: !!session?.user?.uuid,
    onToggle: (event: React.MouseEvent) => handleFavoriteToggle(event),
  };
}

export function useTeamLeagueFavorite(
  id: string,
  initialIsFavorite?: boolean,
  onRefetch?: () => void
) {
  const { session } = useSession();

  const { isFavorited, isDisabled, handleFavoriteToggle } = useFavoriteToggle({
    moduleUuid: id,
    moduleFor: TEAM_LEAGUES_MODULE,
    initialIsFavorite,
    onRefetch,
  });

  return {
    isFavorite: isFavorited,
    isFavoriteDisabled: isDisabled,
    isUserLoggedIn: !!session?.user?.uuid,
    onToggle: (event: React.MouseEvent) => handleFavoriteToggle(event),
  };
}
